import { Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import CustomDateRangePicker from "../../../components/common/DateRangePicker/DateRangePicker.tsx";
import FileUploader from "../../../components/common/FileUploader/FileUploader.tsx";
import { TOON_BOOM_ANIMATION_ORG_ID } from "../../../constants/OrgConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { getSelectedOrganization } from "../../../helpers/localStorage.ts";
import { useAttributionReports } from "../../../hooks/useAttributionReports.tsx";
import { useAppStore } from "../../../hooks/useStores.tsx";
import TabsLayout from "../../../layout/TabsLayout/TabsLayout.tsx";
import { getTodayDateRange, isBriaOrg } from "../../../utils";
import styles from "./AttributionReports.module.scss";

const AttributionReports = () => {
	const navigate = useNavigate();
	const { t } = useTranslation("translation", { keyPrefix: "account.attributionReports" });
	const { authStore, attributionReportsStore } = useAppStore();
	const { exportReport, getSelectedReport, isSelectedReportLoading } = useAttributionReports();
	const [dateRange, setDateRange] = useState<[Date, Date]>(
		attributionReportsStore.selectedDateRange ?? getTodayDateRange(),
	);

	useEffect(() => {
		if (authStore.user?.isSuperAdmin() && isBriaOrg()) {
			navigate(RouterConstants.GENERATION_REPORT.fullPath, { replace: true });
		} else {
			navigate(RouterConstants.CATALOG_REPORT.fullPath, { replace: true });
		}
	}, []);

	useEffect(() => {
		if (
			dateRange[0] !== attributionReportsStore.selectedDateRange[0] ||
			dateRange[1] !== attributionReportsStore.selectedDateRange[1]
		) {
			attributionReportsStore.resetStore();
			attributionReportsStore.selectedDateRange = dateRange;
		}
	}, [dateRange]);

	let subTabs: string[];
	if (authStore.user?.isSuperAdmin() && isBriaOrg()) {
		subTabs = ["generation-report", "catalog-report", "top-images", "partners-earnings"];
	} else if (
		!authStore.user?.isSuperAdmin() &&
		getSelectedOrganization()?.organization.uid === TOON_BOOM_ANIMATION_ORG_ID
	) {
		subTabs = ["catalog-report"];
	} else {
		subTabs = ["catalog-report", "top-images"];
	}

	const handleDownloadingReports = () => {
		exportReport();
	};
	const handleSelectDateRange = (range: Date[]) => {
		if (range && range.length === 2 && range[0] instanceof Date && range[1] instanceof Date) {
			setDateRange([range[0], range[1]]);
		}
	};
	const secondaryButton = (
		<FileUploader
			inputProps={{ accept: ".csv,text/csv" }}
			buttonText={<Typography>{t("uploadFinancials")}</Typography>}
			disabled={
				attributionReportsStore.isTopImagesDataLoading ||
				!attributionReportsStore.topImagesData.length ||
				Boolean(attributionReportsStore.topImagesData[0]?.topPaid?.length)
			}
			onUpload={(e) => (attributionReportsStore.topPaidImagesPPGFile = e.target.files && e.target.files[0])}
			loading={attributionReportsStore.isTopImagesDataLoading}
		/>
	);
	return (
		<>
			<TabsLayout
				className={styles.reportsTabsLayout}
				title={attributionReportsStore.selectedTab}
				subTabs={subTabs}
				primaryButtonLabel={t("primaryButton")}
				primaryButtonClickHandler={handleDownloadingReports}
				primaryButtonLoading={attributionReportsStore.isExportReportLoading}
				primaryButtonDisabled={
					attributionReportsStore.isExportReportLoading ||
					!getSelectedReport().length ||
					isSelectedReportLoading()
				}
				secondaryButtonLabel={
					authStore.user?.isSuperAdmin() &&
					isBriaOrg() &&
					attributionReportsStore.selectedTab === t("tabs.topImages") &&
					secondaryButton
				}
				extraComponent={
					<CustomDateRangePicker
						loading={false}
						placement="autoVerticalEnd"
						value={dateRange}
						onChange={handleSelectDateRange}
						cleanable={false}
					/>
				}
			>
				<Outlet />
			</TabsLayout>
		</>
	);
};

export default observer(AttributionReports);
